<template>
  <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
    <!-- Actionable features section -->
    <div class="px-4 py-3 bg-gradient-to-r from-purple-50 to-indigo-50 border-b border-slate-100">
      <div class="flex items-center gap-2">
        <Sparkles class="w-4 h-4 text-purple-600" />
        <span class="font-bold text-sm text-slate-800">מופעל עכשיו</span>
      </div>
    </div>
    <div class="divide-y divide-slate-100">
      <div
        v-for="feature in actionableFeatures"
        :key="feature.feature"
        class="flex items-start gap-3 px-4 py-3 transition-colors"
        :class="feature.enabled ? '' : 'opacity-60'"
      >
        <div class="flex-1 min-w-0">
          <span class="text-sm font-medium text-slate-800">{{ feature.displayName }}</span>
          <p class="text-xs text-slate-500 mt-0.5 leading-relaxed">{{ feature.reasoning || feature.description }}</p>
          <p v-if="feature.enabled && feature.suggestedConfig" class="text-xs text-purple-600 mt-1 font-medium">
            {{ formatSuggestedConfig(feature) }}
          </p>
        </div>
        <InputSwitch
          :modelValue="feature.enabled"
          @update:modelValue="$emit('toggle-feature', feature.feature)"
          :pt="{
            root: { class: 'w-10 h-5 flex-shrink-0 mt-0.5' },
            slider: { class: 'before:w-4 before:h-4' },
          }"
        />
      </div>
    </div>

    <!-- Explain later features section -->
    <template v-if="explainLaterFeatures.length > 0">
      <div class="px-4 py-3 bg-gradient-to-r from-amber-50 to-orange-50 border-t border-b border-slate-100">
        <div class="flex items-center gap-2">
          <Lightbulb class="w-4 h-4 text-amber-600" />
          <span class="font-bold text-sm text-slate-800">מומלץ להפעיל בהמשך</span>
        </div>
      </div>
      <div class="divide-y divide-slate-100">
        <div
          v-for="feature in explainLaterFeatures"
          :key="feature.feature"
          class="px-4 py-3"
        >
          <div class="flex items-center gap-2 mb-1">
            <span class="text-sm font-medium text-slate-800">{{ feature.displayName }}</span>
            <span class="text-[10px] bg-amber-100 text-amber-700 font-medium px-2 py-0.5 rounded-full">בהמשך</span>
          </div>
          <p class="text-xs text-slate-500 leading-relaxed">{{ feature.reasoning || feature.description }}</p>
          <p v-if="feature.setupInstructions" class="text-xs text-amber-600 mt-1 leading-relaxed">
            {{ feature.setupInstructions }}
          </p>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { IOnboardingSuggestedFeature } from '@torim-app/types';
import InputSwitch from 'primevue/inputswitch';
import { Sparkles, Lightbulb } from 'lucide-vue-next';

const props = defineProps<{
  features: IOnboardingSuggestedFeature[];
}>();

defineEmits<{
  'toggle-feature': [feature: string];
}>();

const actionableFeatures = computed(() =>
  props.features.filter(f => f.category === 'actionable')
);

const explainLaterFeatures = computed(() =>
  props.features.filter(f => f.category === 'explain_later')
);

const formatSuggestedConfig = (feature: IOnboardingSuggestedFeature): string => {
  if (!feature.suggestedConfig) return '';

  const config = feature.suggestedConfig;
  if (config.promoType === 'discount' && config.numberOfAppointments && config.discountAmount) {
    return `הנחה של ${config.discountAmount}% על הביקור שאחרי ${config.numberOfAppointments} ביקורים`;
  }
  if (config.syncWithHolidays) {
    return 'סנכרון אוטומטי עם חגי ישראל';
  }
  if (config.enabled) {
    return 'מופעל';
  }
  return '';
};
</script>
